import { ChemistrySample } from "@/pages/caster/models/ElementData";
import { ScrapData } from "@/pages/caster/models/ScrapData";
import { StrandTimeSeriesData } from "@/pages/caster/models/StrandSpeed";
import { CatalogGradeMechanicalLimit } from "@/pages/laboratory/models/CatalogGradeMechanicalLimit";
import { CatalogMechanicalTest } from "@/pages/laboratory/models/CatalogMechanicalTest";
import { CatalogResultSource } from "@/pages/laboratory/models/CatalogResultSource";
import { MechanicalSample } from "@/pages/laboratory/models/MechanicalSample";
import type { Entry } from "@dexteel/mesf-core";
import { createSlice } from "@reduxjs/toolkit";
import {
	Addition,
	CasterProcessData,
	Charge,
	EAFProcessData,
	HeatData,
	HeatLabData,
	HeatReportDelay,
	Heating,
	Phase,
	Probe,
	Production,
	StrandData,
} from "../models/HeatReportModels";

const HeatReportInitialState: {
	isDataLoading: boolean;
	heat: HeatData;
	eafProcessData: EAFProcessData;
	casProcessData: CasterProcessData;
	heatLabData: HeatLabData | null;
	elementData: ChemistrySample[];
	productionData: Production[];
	strandData: StrandData[];
	scrapData: ScrapData[];
	probes: Probe[];
	charges: Charge[];
	additions: Addition[];
	heatings: Heating[];
	phases: Phase[];
	delays: HeatReportDelay[];
	logbook: Entry[];
	strandSpeeds: StrandTimeSeriesData[];
	moldLevels: StrandTimeSeriesData[];
	eafValues: StrandTimeSeriesData[];
	billetTemp: StrandTimeSeriesData[];
	// Mechanical test data
	mechanicalTests: CatalogMechanicalTest[] | null;
	mechanicalSamples: MechanicalSample[] | null;
	mechanicalGradeLimits: CatalogGradeMechanicalLimit[] | null;
	mechanicalResultSources: CatalogResultSource[] | null;
	isMechanicalDataLoading: boolean;
	eafChartOffsetMinutes: number;
} = {
	isDataLoading: false,
	heat: {
		HeatName: "",
		HeatId: 0,
		Grade: "",
		LadleName: null,
		HeatsOnLadle: null,
		TapDate: null,
		TapShift: null,
		TapCrew: null,
		OpenDate: null,
		OpenShift: null,
		OpenCrew: null,
		FinalStatus: null,
		SteelWeight: null,
		EAFProcessId: null,
		CASProcessId: null,
		NextHeatId: null,
		PreviousHeatId: null,
		NextHeatName: null,
		PreviousHeatName: null,
	},
	eafProcessData: {
		ProcessId: 0,
		Start: new Date(),
		End: null,
		StartTap: null,
		TapToTap: 0,
		PowerOn: 0,
		PowerOff: 0,
		ScrapCharged: null,
		Energy: 0,
		KWHPerTon: null,
		Foreman: null,
		Lime: 0,
		OxygenVolume: null,
		HeatsOnRoof: null,
		HeatsOnLining: null,
		HeatsOnTapHole: null,
		HeatsOnRunnerSpout: null,
		ElectrodeConsumption: null,
		ArcStability: null,
		Comments: null,
		DelayTime: null,
		PrevHeatId: null,
		NextHeatId: null,
		PrevHeatName: null,
		NextHeatName: null,
		AssetId: 0,
		AssetName: "",
	},
	casProcessData: {
		ProcessId: 0,
		AssetId: 0,
		AssetName: "",
		IsFreeOpen: false,
		LadleOpen: null,
		LadleClose: null,
		Duration: 0,
		NozzleSize: null,
		HeatsOnNozzle: null,
		TundishName: null,
		HeatsOnTundish: null,
		TundishShroudName: null,
		HeatsOnTundishShroud: null,
		Foreman: null,
		PourbackWeight: null,
		CastTons: null,
		Comments: null,
	},
	heatLabData: null,
	elementData: [],
	productionData: [],
	strandData: [],
	scrapData: [],
	probes: [],
	charges: [],
	additions: [],
	heatings: [],
	phases: [],
	delays: [],
	logbook: [],
	strandSpeeds: [],
	moldLevels: [],
	eafValues: [],
	billetTemp: [],
	// Mechanical test data
	mechanicalTests: null,
	mechanicalSamples: null,
	mechanicalGradeLimits: null,
	mechanicalResultSources: null,
	isMechanicalDataLoading: false,
	eafChartOffsetMinutes: 40,
};

export const HeatReportReducer = createSlice({
	name: "__",
	initialState: HeatReportInitialState,
	reducers: {
		setIsDataLoading(state, { payload }) {
			state.isDataLoading = payload;
		},
		setHeat(state, { payload }) {
			state.heat = payload;
		},
		setEAFProcessData(state, { payload }) {
			state.eafProcessData = payload;
		},
		setCASProcessData(state, { payload }) {
			state.casProcessData = payload;
		},
		setHeatLabData(state, { payload }) {
			state.heatLabData = payload;
		},
		setElementData(state, { payload }) {
			state.elementData = payload;
		},
		setProductionData(state, { payload }) {
			state.productionData = payload;
		},
		setStrandData(state, { payload }) {
			state.strandData = payload;
		},
		setScrapData(state, { payload }) {
			state.scrapData = payload;
		},
		setProbes(state, { payload }) {
			state.probes = payload;
		},
		setCharges(state, { payload }) {
			state.charges = payload;
		},
		setAdditions(state, { payload }) {
			state.additions = payload;
		},
		setHeatings(state, { payload }) {
			state.heatings = payload;
		},
		setPhases(state, { payload }) {
			state.phases = payload;
		},
		setDelays(state, { payload }) {
			state.delays = payload;
		},
		setLogbook(state, { payload }) {
			state.logbook = payload;
		},
		setStrandSpeeds(state, { payload }) {
			state.strandSpeeds = payload;
		},
		setMoldLevels(state, { payload }) {
			state.moldLevels = payload;
		},
		setEAFValues(state, { payload }) {
			state.eafValues = payload;
		},
		setBilletTemp(state, { payload }) {
			state.billetTemp = payload;
		},
		// Mechanical test actions
		setMechanicalTests(state, { payload }) {
			state.mechanicalTests = payload;
		},
		setMechanicalSamples(state, { payload }) {
			state.mechanicalSamples = payload;
		},
		setMechanicalGradeLimits(state, { payload }) {
			state.mechanicalGradeLimits = payload;
		},
		setMechanicalResultSources(state, { payload }) {
			state.mechanicalResultSources = payload;
		},
		setIsMechanicalDataLoading(state, { payload }) {
			state.isMechanicalDataLoading = payload;
		},
		setEafChartOffsetMinutes(state, { payload }) {
			state.eafChartOffsetMinutes = payload;
		},
	},
});
